import { Buffer } from "buffer";
import {
  FC,
  Suspense,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from "react";
import {
  PreloadedQuery,
  useFragment,
  useLazyLoadQuery,
  usePreloadedQuery,
  useQueryLoader,
} from "react-relay";
import { graphql } from "relay-runtime";
import { Account, Asset, MsgExecute, MsgMulti } from "rujira.js";
import { coins } from "rujira.js/src/signers/cosmos";
import { Button, SwapSelect } from "rujira.ui";
import { MsgProvider, TxButton } from "../../common/components/TxButton";
import { useOraclePrice } from "../../common/OraclePrice";
import { useAccounts } from "../../services/accounts";
import { Subscription } from "../../services/useNodeSubscription";
import { calculateReturnAmount } from "../../trade/utils";
import { PositionSwapBookFragment$key } from "./__generated__/PositionSwapBookFragment.graphql";
import { PositionSwapConfigQuery } from "./__generated__/PositionSwapConfigQuery.graphql";
import { PositionSwapQuery } from "./__generated__/PositionSwapQuery.graphql";
import { AdjustmentSummary } from "./Common";
import { CdpSingle } from "./utils";

const configQuery = graphql`
  query PositionSwapConfigQuery($query: String!) {
    finV2(first: 5, query: $query, sortBy: VOLUME_USD, sortDir: DESC) {
      edges {
        node {
          id
          address
          assetBase {
            asset
            metadata {
              symbol
            }
          }
          assetQuote {
            asset
            metadata {
              symbol
            }
          }
        }
      }
    }
    ghostCredit {
      address
      collaterals {
        asset {
          asset
          type
          chain
          metadata {
            decimals
            symbol
          }
          variants {
            native {
              denom
            }
          }
        }
        ratio
      }
    }
  }
`;

const query = graphql`
  query PositionSwapQuery($id: ID!, $oracle: ID!) {
    positionSwapContract: node(id: $id) {
      ... on FinPair {
        id
        book {
          ...PositionSwapBookFragment
        }
      }
    }
    oraclePrice: node(id: $oracle) {
      id
      ...OraclePriceFragment
    }
  }
`;

const f = graphql`
  fragment PositionSwapBookFragment on FinBook {
    id
    pair {
      assetBase {
        asset
      }
      assetQuote {
        asset
      }
    }
    bids {
      price
      total
      value
    }
    asks {
      price
      total
      value
    }
  }
`;

const sub1 = graphql`
  subscription PositionSwapBookSubscription($id: ID!) {
    node(id: $id) {
      ... on FinBook {
        ...PositionSwapBookFragment
      }
    }
  }
`;

const sub2 = graphql`
  subscription PositionSwapOracleSubscription($id: ID!) {
    node(id: $id) {
      ... on ThorchainOraclePrice {
        ...OraclePriceFragment
      }
    }
  }
`;

export const Swap: FC<{
  hideModal: () => void;
  p: CdpSingle<Asset>;
  registry: string;
}> = ({ hideModal, p, registry }) => {
  const data = useLazyLoadQuery<PositionSwapConfigQuery>(configQuery, {
    query: p.collateral.asset.metadata.symbol,
  });

  const options = (data.ghostCredit?.collaterals || [])
    .filter((x) => x.asset.asset !== p.collateral.asset.asset)
    .map((x) => ({ asset: x.asset }));
  const [to, setTo] = useState<Asset>(
    options.find((a) =>
      p.collateral.asset.asset === "BTC-BTC"
        ? a.asset.asset === "ETH-ETH"
        : a.asset.asset === "BTC-BTC"
    )?.asset || options[0].asset
  );
  const target = data.finV2.edges?.find(
    (a) =>
      a?.node &&
      to &&
      [a.node.assetBase.asset, a.node.assetQuote.asset].includes(to.asset)
  )?.node!;
  const max = p.collateral.amount;

  const { selected } = useAccounts();

  const msg = useMemo(() => {
    const collateralDenom = p.collateral.asset.variants?.native?.denom;
    if (!p.collateral.amount) return null;
    if (!collateralDenom) return null;
    if (!selected) return null;
    if (!target) return null;
    const acc: Account = { address: selected.address, network: "THOR" };

    return new MsgMulti(acc, [
      new MsgExecute(acc, [], registry, {
        account: {
          addr: p.account.address,
          msgs: [
            {
              execute: {
                contract_addr: target.address,
                msg: Buffer.from(JSON.stringify({ swap: {} })).toString(
                  "base64"
                ),
                funds: coins(p.collateral.amount.toString(), collateralDenom),
              },
            },
          ],
        },
      }),
    ]);
  }, [target]);

  const [q, loadQuery] = useQueryLoader<PositionSwapQuery>(query);
  const [_, transition] = useTransition();

  useEffect(() => {
    if (!target) return;
    transition(() => {
      loadQuery(
        {
          id: target.id,
          oracle: Buffer.from(
            `ThorchainOraclePrice:${p.collateral.asset.asset === target.assetBase.asset ? target.assetQuote.metadata.symbol : target.assetBase.metadata.symbol}`
          ).toString("base64"),
        },
        { fetchPolicy: "store-and-network" }
      );
    });
  }, [target]);

  return (
    <MsgProvider msg={msg}>
      <div style={{ marginLeft: "-0.5rem", marginRight: "-0.5rem" }}>
        <div className="row wrap gap-y-2 pad--sm mt-3">
          <div className="col-12 col-md-6">
            <h3 className="fs-14 fw-500 color-grey mb-1 ml-2">From</h3>
            <SwapSelect
              selected={p.collateral.asset}
              amount={max}
              value={p.collateral.value}
              full
              className="bg-black"
            />
          </div>
          <Suspense
            fallback={
              <To
                p={p}
                asset={to}
                amount={0n}
                onSelect={setTo}
                options={options}
              />
            }>
            {q ? (
              <Simulate
                q={q}
                p={p}
                asset={to}
                amount={p.collateral.amount}
                onSelect={setTo}
                options={options}
              />
            ) : (
              <To
                p={p}
                asset={to}
                amount={0n}
                onSelect={setTo}
                options={options}
              />
            )}
          </Suspense>
        </div>
      </div>
      <div className="modal__footer mt-4 px-3 py-2 flex ai-s jc-e wrap">
        <Button
          className="button--grey button--outline"
          onClick={hideModal}
          label="Cancel"
        />
        <div className="block ml-a text-right">
          {target ? (
            <TxButton label="Swap Collateral" />
          ) : (
            <Button disabled label="No Swap Route Found" />
          )}
        </div>
      </div>
    </MsgProvider>
  );
};

const Simulate: FC<{
  q: PreloadedQuery<PositionSwapQuery>;
  p: CdpSingle<Asset>;
  asset: Asset;
  amount: bigint;
  options: {
    asset: Asset;
  }[];

  onSelect: (opt: Asset) => void;
}> = ({ asset, amount, onSelect, q, options, p }) => {
  const x = usePreloadedQuery(query, q);
  const data = useFragment<PositionSwapBookFragment$key>(
    f,
    x.positionSwapContract?.book
  );

  const bids = data
    ? asset.asset === data.pair.assetQuote.asset
      ? data?.bids
      : data?.asks
    : [];

  const returned = calculateReturnAmount([...bids], amount);
  const price = useOraclePrice(x.oraclePrice);
  const swapped =
    price && returned ? CdpSingle.swapCollateral(p, returned, price) : p;

  return (
    <>
      {data && <Subscription subscription={sub1} id={data.id} />}
      {x.oraclePrice?.id && (
        <Subscription subscription={sub2} id={x.oraclePrice.id} />
      )}
      <To
        p={swapped}
        asset={asset}
        amount={returned}
        onSelect={onSelect}
        options={options}
      />
    </>
  );
};

const To: FC<{
  p: CdpSingle<Asset>;
  asset: Asset;
  amount: bigint;
  onSelect: (opt: Asset) => void;
  options: {
    asset: Asset;
  }[];
}> = ({ p, asset, amount, onSelect, options }) => (
  <>
    <div className="col-12 col-md-6">
      <h3 className="fs-14 fw-500 color-grey mb-1 ml-2">To</h3>

      <SwapSelect
        selected={asset}
        value={p.collateral.value}
        onSelect={onSelect}
        options={
          options.length > 1
            ? options.map((x) => ({ asset: x.asset }))
            : undefined
        }
        amount={amount}
        full
        className="bg-black"
      />
    </div>

    <AdjustmentSummary p={p} />
  </>
);
